import { useEffect, useState } from 'react';

/**
 * Черновик раздела карточки организации.
 *
 * Разделы правятся и сохраняются по одному, но данные у них общие — один запрос
 * `GET /api/admin/settings`. Черновик снимается с загруженных данных РОВНО один
 * раз: react-query обновляет их в фоне (в том числе после сохранения соседнего
 * раздела), и без этого правило «последнее слово за сервером» стирало бы то,
 * что человек уже набрал, но ещё не сохранил.
 *
 * `base` — то, что лежит на сервере по нашим сведениям. От него считается «есть
 * ли что сохранять», и он же сдвигается после успешного сохранения: иначе
 * кнопка оставалась бы активной, а строка состояния — врущей.
 */
export function useOrgDraft<T>(loaded: T | null) {
  const [draft, setDraft] = useState<T | null>(null);
  const [base, setBase] = useState<T | null>(null);

  useEffect(() => {
    if (!loaded || draft) return;
    setDraft(loaded);
    setBase(loaded);
  }, [loaded, draft]);

  const dirty = !!draft && !!base && JSON.stringify(draft) !== JSON.stringify(base);

  /**
   * Правка через функцию, а не через `{...draft, ...p}`: логотип приезжает уже
   * после диалога выбора файла, и черновик к тому моменту мог уехать вперёд.
   */
  const patch = (p: Partial<T>) => setDraft((prev) => (prev ? { ...prev, ...p } : prev));

  /** Сохранено: с этого момента «есть ли правки» считается от нового значения. */
  const commit = (next: T) => { setDraft(next); setBase(next); };

  return { draft, base, dirty, patch, setDraft, commit };
}
